import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Package, ChevronDown, ChevronRight, Gem, Award } from 'lucide-react';
import usePermissions from '../../hooks/usePermissions';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import api from '../../services/api';

const CategoryManagement = () => {
  const { hasPermission, getPermissionLevel } = usePermissions();
  
  // Permission checks for category-management page
  const canCreate = hasPermission('category-management', 'create');
  const canEdit = hasPermission('category-management', 'edit');
  const canDelete = hasPermission('category-management', 'delete');
  // const canView = hasPermission('category-management', 'view'); // Not used, route protection handles this
  const permissionLevel = getPermissionLevel('category-management');
  const [showCreateNewMaterial, setShowCreateNewMaterial] = useState(false);
  const [newMaterialName, setNewMaterialName] = useState('');
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false); // Changed to false by default
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [categoryType, setCategoryType] = useState('parent'); // 'parent' or 'child'
  const [newCategory, setNewCategory] = useState({
    name: '',
    code: '',
    description: '',
    wastageCharges: 0,
    makingCharges: 0,
    certificationCharges: 0,
    purchaseWastagePct: 0,
    purchaseMakingPerG: 0,
    saleWastagePct: 0,
    saleMakingPerG: 0,
    parentId: null,
    type: 'parent' // 'parent' or 'child'
  });
  const [editingCategory, setEditingCategory] = useState({
    name: '',
    code: '',
    description: '',
    wastageCharges: 0,
    makingCharges: 0,
    certificationCharges: 0,
    purchaseWastagePct: 0,
    purchaseMakingPerG: 0,
    saleWastagePct: 0,
    saleMakingPerG: 0,
    parentId: null,
    type: 'parent'
  });
  const [jewelryCategories, setJewelryCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
      const data = await api.getCategories();
        setJewelryCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Set empty array to prevent component from breaking
        setJewelryCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  if (loading) {
    return <div>Loading categories...</div>;
  }

  // Get parent categories (Jewelry Categories)
  const getParentCategories = () => {
    const parents = jewelryCategories.filter(cat => 
      cat.type === 'parent' && 
      !cat.parent_id && 
      cat.is_active === true
    );
    return parents;
  };

  // Get child categories for a specific parent
  const getChildCategories = (parentId) => {
    const children = jewelryCategories.filter(cat => cat.parent_id === parentId && cat.type === 'child');
    return children;
  };

  // Get all child categories (Product Types)
  const getAllChildCategories = () => {
    const children = jewelryCategories.filter(cat => cat.type === 'child');
    return children;
  };

  // Get the appropriate screen ID based on the current form state
  const getCurrentScreenId = () => {
    if (showAddCategoryForm) {
      if (categoryType === 'child') {
        if (showCreateNewMaterial) {
          return SCREEN_IDS?.CATEGORIES?.INLINE_NEW_CATEGORY || 'CAT-005';
        }
        return SCREEN_IDS?.CATEGORIES?.ADD_PRODUCT_TYPE || 'CAT-002';
      } else if (categoryType === 'parent') {
        return SCREEN_IDS?.CATEGORIES?.ADD_JEWELRY_CATEGORY || 'CAT-003';
      }
    }
    return SCREEN_IDS?.CATEGORIES?.MAIN || 'CAT-001';
  };

  // Get the appropriate page name based on the current form state
  const getCurrentPageName = () => {
    if (showAddCategoryForm) {
      if (categoryType === 'child') {
        if (showCreateNewMaterial) {
          return 'Add Product Type - Create New Jewelry Category';
        }
        return 'Add Product Type';
      } else if (categoryType === 'parent') {
        return 'Add Jewelry Category';
      }
    }
    return 'Category Management';
  };

  // Toggle expansion of parent category
  const toggleExpansion = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleAddCategory = async () => {
    // For parent categories, only name is required
    // For child categories, name, code, and parentId are required
    const isValid = categoryType === 'parent'
      ? newCategory.name.trim()
      : newCategory.name.trim() && newCategory.code.trim() && newCategory.parentId;

    if (!isValid) {
      if (categoryType === 'child') {
        if (!newCategory.name.trim()) {
          alert('Product Type name is required');
          return;
        }
        if (!newCategory.code.trim()) {
          alert('Product Type code is required');
          return;
        }
        if (!newCategory.parentId) {
          alert('Please select a Jewelry Category for this Product Type');
          return;
        }
      } else {
        alert('Jewelry Category name is required');
        return;
      }
    }

    if (isValid) {
      // Check if code already exists (only for child categories)
      if (categoryType === 'child' && newCategory.code) {
        const codeExists = jewelryCategories.some(cat =>
          cat.code && cat.code.toLowerCase() === newCategory.code.toLowerCase()
        );

        if (codeExists) {
          alert('Category code already exists. Please use a different code.');
          return;
        }
      }

      try {
        // Prepare data for API call
        const categoryToAdd = {
          name: newCategory.name.trim(),
          type: categoryType,
          parent_id: categoryType === 'child' ? newCategory.parentId : null,
          code: categoryType === 'parent' ? null : newCategory.code?.trim(),
          description: newCategory.description?.trim() || '',
          wastage_charges: newCategory.saleWastagePct || 0,
          making_charges: newCategory.saleMakingPerG || 0,
          certification_charges: newCategory.certificationCharges || 0,
          purchase_wastage_pct: newCategory.purchaseWastagePct || 0,
          purchase_making_per_g: newCategory.purchaseMakingPerG || 0,
          sale_wastage_pct: newCategory.saleWastagePct || 0,
          sale_making_per_g: newCategory.saleMakingPerG || 0,
          is_active: true
        };

        // Save to database via API
        const savedCategory = await api.createCategory(categoryToAdd);
        
        // Refetch all categories to ensure fresh data
        const freshCategories = await api.getCategories();
        setJewelryCategories(freshCategories);
        
        // Show success message
        const categoryTypeName = categoryType === 'parent' ? 'Jewelry Category' : 'Product Type';
        alert(`${categoryTypeName} "${savedCategory.name}" added successfully!`);
        
        // Reset form
        setNewCategory({
          name: '',
          code: '',
          description: '',
          wastageCharges: 0,
          makingCharges: 0,
          certificationCharges: 0,
          purchaseWastagePct: 0,
          purchaseMakingPerG: 0,
          saleWastagePct: 0,
          saleMakingPerG: 0,
          parentId: null,
          type: 'parent'
        });
        setCategoryType('parent');
        setShowAddCategoryForm(false);
        
      } catch (error) {
        console.error('Error saving category:', error);
        alert(`Failed to save ${categoryType === 'parent' ? 'Jewelry Category' : 'Product Type'}. Please try again.`);
      }
    }
  };

  const handleCreateNewJewelryCategory = async () => {
    if (newMaterialName.trim()) {
      try {
        // Prepare data for API call
        const newMaterial = {
          name: newMaterialName.trim(),
          code: null,
          description: '',
          wastage_charges: 0,
          making_charges: 0,
          certification_charges: 0,
          parent_id: null,
          type: 'parent',
          is_active: true
        };

        // Save to database via API
        const savedMaterial = await api.createCategory(newMaterial);
        
        // Update local state with the saved category (includes generated ID)
        setJewelryCategories(prev => [...prev, savedMaterial]);
        
        // Show success message and update form
        alert(`Jewelry Category "${savedMaterial.name}" created successfully!`);
        setNewCategory(prev => ({ ...prev, parentId: savedMaterial.id }));
        setNewMaterialName('');
        setShowCreateNewMaterial(false);
        
      } catch (error) {
        console.error('Error creating jewelry category:', error);
        alert('Failed to create Jewelry Category. Please try again.');
      }
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategoryId(category.id);
    // Map database field names to form state field names with safe number conversion
    const safeNumber = (value) => isNaN(Number(value)) ? 0 : Number(value);
    
    setEditingCategory({ 
      ...category,
      wastageCharges: safeNumber(category.wastage_charges),
      makingCharges: safeNumber(category.making_charges),
      certificationCharges: safeNumber(category.certification_charges),
      purchaseWastagePct: safeNumber(category.purchase_wastage_pct),
      purchaseMakingPerG: safeNumber(category.purchase_making_per_g),
      saleWastagePct: safeNumber(category.sale_wastage_pct) || safeNumber(category.wastage_charges),
      saleMakingPerG: safeNumber(category.sale_making_per_g) || safeNumber(category.making_charges)
    });
  };

  const handleSaveEdit = async () => {
    if (!editingCategory.name || !editingCategory.name.trim()) {
      alert('Category name is required.');
      return;
    }
    if (editingCategory.type === 'child' && (!editingCategory.code || !editingCategory.code.trim())) {
      alert('Product Type code is required.');
      return;
    }

    // Check if code already exists (only for child categories, excluding current category)
    if (editingCategory.type === 'child' && editingCategory.code) {
        const codeExists = jewelryCategories.some(cat =>
          cat.id !== editingCategoryId &&
          cat.code &&
          cat.code.toLowerCase() === editingCategory.code.toLowerCase()
        );

        if (codeExists) {
          alert('Category code already exists. Please use a different code.');
          return;
        }
      }

      try {
        // Prepare data for API call
        const updatedCategory = {
          name: editingCategory.name.trim(),
          type: editingCategory.type,
          parent_id: editingCategory.parent_id,
          code: editingCategory.type === 'parent' ? null : editingCategory.code?.trim(),
          description: editingCategory.description?.trim() || '',
          wastage_charges: editingCategory.saleWastagePct || 0,
          making_charges: editingCategory.saleMakingPerG || 0,
          certification_charges: editingCategory.certificationCharges || 0,
          purchase_wastage_pct: editingCategory.purchaseWastagePct || 0,
          purchase_making_per_g: editingCategory.purchaseMakingPerG || 0,
          sale_wastage_pct: editingCategory.saleWastagePct || 0,
          sale_making_per_g: editingCategory.saleMakingPerG || 0,
          is_active: editingCategory.is_active !== false
        };

        // Save to database via API
        const savedCategory = await api.updateCategory(editingCategoryId, updatedCategory);
        
        // Refetch all categories to ensure fresh data (including new columns)
        const freshCategories = await api.getCategories();
        setJewelryCategories(freshCategories);
        
        // Reset editing state
        setEditingCategoryId(null);
        setEditingCategory({
          name: '',
          code: '',
          description: '',
          wastageCharges: 0,
          makingCharges: 0,
          certificationCharges: 0,
          purchaseWastagePct: 0,
          purchaseMakingPerG: 0,
          saleWastagePct: 0,
          saleMakingPerG: 0,
          parentId: null,
          type: 'parent'
        });
        
        alert(`Category "${savedCategory.name}" updated successfully!`);
        
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingCategoryId(null);
    setEditingCategory({
      name: '',
      code: '',
      description: '',
      wastageCharges: 0,
      makingCharges: 0,
      certificationCharges: 0,
      purchaseWastagePct: 0,
      purchaseMakingPerG: 0,
      saleWastagePct: 0,
      saleMakingPerG: 0,
      parentId: null,
      type: 'parent'
    });
  };

  const handleDeleteCategory = async (id) => {
    const category = jewelryCategories.find(cat => cat.id === id);

    // Check if it's a parent with children
    if (category.type === 'parent' || !category.parent_id) {
      const childrenCount = getChildCategories(id).length;
      if (childrenCount > 0) {
        alert(`Cannot delete "${category.name}" because it has ${childrenCount} sub-categories. Please remove or reassign the sub-categories first.`);
        return;
      }
    }

    if (window.confirm(`Are you sure you want to delete the category "${category.name}"?`)) {
      try {
        // Delete from database via API
        await api.deleteCategory(id);
        
        // Update local state by removing the category
        setJewelryCategories(prev => prev.filter(cat => cat.id !== id));
        
        alert(`Category "${category.name}" deleted successfully!`);
        
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category. Please try again.');
      }
    }
  };

  const renderCategoryRow = (category, level = 0) => {
    const isEditing = editingCategoryId === category.id;
    const children = getChildCategories(category.id);
    const isExpanded = expandedCategories.has(category.id);
    const isParent = category.type === 'parent' || !category.parent_id;

    return (
      <React.Fragment key={category.id}>
        <tr className={`hover:bg-gray-50 ${level > 0 ? 'bg-blue-50' : ''}`}>
          <td className="px-6 py-4">
            <div className="flex items-center" style={{ paddingLeft: `${level * 20}px` }}>
              {isParent && children.length > 0 && (
                <button
                  onClick={() => toggleExpansion(category.id)}
                  className="mr-2 p-1 hover:bg-gray-200 rounded"
                >
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              )}
              {isParent ? <Gem className="mr-2 text-blue-500" size={16} /> : <Award className="mr-2 text-green-500" size={16} />}
              {isEditing ? (
                <input
                  type="text"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="Category name"
                />
              ) : (
                <span className={`font-medium ${isParent ? 'text-blue-800' : 'text-green-800'}`}>
                  {category.name}
                </span>
              )}
            </div>
          </td>
          <td className="px-6 py-4">
            {isEditing ? (
              // Only show code input for child categories
              editingCategory.type === 'child' || editingCategory.parent_id ? (
                <input
                  type="text"
                  value={editingCategory.code || ''}
                  onChange={(e) => setEditingCategory(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="CODE"
                  maxLength="10"
                />
              ) : (
                <span className="text-gray-400 italic text-sm">No code needed</span>
              )
            ) : (
              // Display code only for child categories
              (category.type === 'child' || category.parent_id) && category.code ? (
                <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                  {category.code}
                </code>
              ) : (
                <span className="text-gray-400 italic text-sm">—</span>
              )
            )}
          </td>
          <td className="px-6 py-4">
            {isEditing ? (
              <input
                type="text"
                value={editingCategory.description}
                onChange={(e) => setEditingCategory(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                placeholder="Description"
              />
            ) : (
              <span className="text-gray-600">{category.description}</span>
            )}
          </td>
          <td className="px-4 py-4">
            {isEditing ? (
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500 w-16">Purchase</span>
                  <input
                    type="number" step="0.001" min="0" max="100"
                    value={editingCategory.purchaseWastagePct || 0}
                    onChange={(e) => setEditingCategory(prev => ({ ...prev, purchaseWastagePct: parseFloat(e.target.value) || 0 }))}
                    className="w-20 p-1 border rounded text-xs focus:ring-2 focus:ring-blue-500"
                    placeholder="0.000"
                  />
                  <span className="text-xs text-gray-400">%</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500 w-16">Sale</span>
                  <input
                    type="number" step="0.001" min="0" max="100"
                    value={editingCategory.saleWastagePct || 0}
                    onChange={(e) => setEditingCategory(prev => ({ ...prev, saleWastagePct: parseFloat(e.target.value) || 0 }))}
                    className="w-20 p-1 border rounded text-xs focus:ring-2 focus:ring-blue-500"
                    placeholder="0.000"
                  />
                  <span className="text-xs text-gray-400">%</span>
                </div>
              </div>
            ) : (
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400 w-16">Purchase</span>
                  <span className="text-blue-600 font-medium">{(Number(category.purchase_wastage_pct) || 0).toFixed(3)}%</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400 w-16">Sale</span>
                  <span className="text-orange-600 font-medium">{(Number(category.sale_wastage_pct) || Number(category.wastage_charges) || 0).toFixed(3)}%</span>
                </div>
              </div>
            )}
          </td>
          <td className="px-4 py-4">
            {isEditing ? (
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500 w-16">Purchase</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={editingCategory.purchaseMakingPerG || 0}
                    onChange={(e) => setEditingCategory(prev => ({ ...prev, purchaseMakingPerG: parseFloat(e.target.value) || 0 }))}
                    className="w-24 p-1 border rounded text-xs focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500 w-16">Sale</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={editingCategory.saleMakingPerG || 0}
                    onChange={(e) => setEditingCategory(prev => ({ ...prev, saleMakingPerG: parseFloat(e.target.value) || 0 }))}
                    className="w-24 p-1 border rounded text-xs focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400 w-16">Purchase</span>
                  <span className="text-blue-600 font-medium">₹{(Number(category.purchase_making_per_g) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400 w-16">Sale</span>
                  <span className="text-green-600 font-medium">₹{(Number(category.sale_making_per_g) || Number(category.making_charges) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}
          </td>
          <td className="px-6 py-4">
            {isEditing ? (
              <input
                type="number"
                step="0.01"
                min="0"
                value={editingCategory.certificationCharges || 0}
                onChange={(e) => setEditingCategory(prev => ({ ...prev, certificationCharges: parseFloat(e.target.value) || 0 }))}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            ) : (
              <span className="text-purple-600 font-medium">
                ₹{(isNaN(Number(category.certification_charges)) ? 0 : Number(category.certification_charges)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </td>
          <td className="px-6 py-4">
            {isEditing ? (
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={editingCategory.type === 'parent' ? !editingCategory.name : !editingCategory.name || !editingCategory.code}
                  className={`p-2 rounded ${
                    (editingCategory.type === 'parent' ? editingCategory.name : editingCategory.name && editingCategory.code)
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  title="Save changes"
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                  title="Cancel editing"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditCategory(category)}
                  disabled={!canEdit}
                  className={`p-2 rounded ${
                    canEdit 
                      ? 'bg-blue-500 text-white hover:bg-blue-600' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  title={!canEdit ? `No permission to edit categories (Level: ${permissionLevel})` : 'Edit category'}
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  disabled={!canDelete}
                  className={`p-2 rounded ${
                    canDelete 
                      ? 'bg-red-500 text-white hover:bg-red-600' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  title={!canDelete ? `No permission to delete categories (Level: ${permissionLevel})` : 'Delete category'}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </td>
        </tr>
        {/* Render children if expanded */}
        {isParent && isExpanded && children.map(child => renderCategoryRow(child, level + 1))}
      </React.Fragment>
    );
  };

  // Calculate parent categories reactively
  // const parentCategories = getParentCategories(); // Not used in current implementation

  return (
    <div className="p-6 pb-12">
      <PageIdentifier pageId={getCurrentScreenId()} pageName={getCurrentPageName()} />
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Product Categories</h2>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setCategoryType('child');
              setShowAddCategoryForm(true);
            }}
            disabled={!canCreate}
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
              canCreate 
                ? 'bg-green-500 text-white hover:bg-green-600' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title={!canCreate ? `No permission to create categories (Level: ${permissionLevel})` : ''}
          >
            <Award size={20} />
            Add Product Type
          </button>
          <button
            onClick={() => {
              setCategoryType('parent');
              setShowAddCategoryForm(true);
            }}
            disabled={!canCreate}
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
              canCreate 
                ? 'bg-blue-500 text-white hover:bg-blue-600' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title={!canCreate ? `No permission to create categories (Level: ${permissionLevel})` : ''}
          >
            <Plus size={20} />
            Add Jewelry Category
          </button>
        </div>
      </div>

      {/* Add Category Form */}
      {showAddCategoryForm && (
        <div className="mb-6 bg-blue-50 p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">
            {categoryType === 'parent' ? 'Add New Jewelry Category:' : 'Add New Product Type:'}
          </h3>

          {/* Category Type Display */}
          <div className="mb-4 p-3 bg-white rounded-lg border">
            <div className="flex items-center">
              {categoryType === 'parent' ? (
                <>
                  <Gem className="mr-2 text-blue-500" size={20} />
                  <span className="font-medium text-blue-700">Creating Jewelry Category</span>
                  <span className="ml-2 text-sm text-gray-600">(e.g., Diamond, Gold, Kundan)</span>
                </>
              ) : (
                <>
                  <Award className="mr-2 text-green-500" size={20} />
                  <span className="font-medium text-green-700">Creating Product Type</span>
                  <span className="ml-2 text-sm text-gray-600">(e.g., Necklace, Ring, Earring)</span>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Parent Selection for Child Categories */}
            {categoryType === 'child' && (
              <div>
                <label className="block text-sm font-medium mb-1">Jewelry Category *</label>
                <div className="space-y-2">
                  <select
                    value={newCategory.parentId || ''}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, parentId: parseInt(e.target.value) || null }))}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Jewelry Category</option>
                    {getParentCategories().map(parent => (
                      <option key={parent.id} value={parent.id}>{parent.name}</option>
                    ))}
                  </select>

                  {/* Quick Create New Material Option */}
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateNewMaterial(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <Plus size={14} className="mr-1" />
                      Create new jewelry category
                    </button>
                  </div>

                  {/* Inline Create New Material */}
                  {showCreateNewMaterial && (
                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <label className="block text-sm font-medium mb-1 text-yellow-800">
                        New Jewelry Category Name
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newMaterialName}
                          onChange={(e) => setNewMaterialName(e.target.value)}
                          className="flex-1 p-2 border rounded focus:ring-2 focus:ring-yellow-500"
                          placeholder="e.g., Silver, Platinum"
                          onKeyPress={(e) => e.key === 'Enter' && handleCreateNewJewelryCategory()}
                        />
                        <button
                          type="button"
                          onClick={handleCreateNewJewelryCategory}
                          disabled={!newMaterialName.trim()}
                          className="bg-yellow-600 text-white px-3 py-2 rounded hover:bg-yellow-700 disabled:bg-gray-300"
                        >
                          Create
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateNewMaterial(false);
                            setNewMaterialName('');
                          }}
                          className="bg-gray-500 text-white px-3 py-2 rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">
                {categoryType === 'parent' ? 'Category Name *' : 'Product Type *'}
              </label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                placeholder={categoryType === 'parent' ? 'e.g., Diamond, Gold' : 'e.g., Necklace, Ring'}
              />
            </div>

            {/* Only show code field for child categories */}
            {categoryType === 'child' && (
              <div>
                <label className="block text-sm font-medium mb-1">Category Code *</label>
                <input
                  type="text"
                  value={newCategory.code}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., DIA-N"
                  maxLength="10"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <input
                type="text"
                value={newCategory.description}
                onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                placeholder="Category description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Purchase Wastage (%)</label>
              <input
                type="number" step="0.001" min="0" max="100"
                value={newCategory.purchaseWastagePct}
                onChange={(e) => setNewCategory(prev => ({ ...prev, purchaseWastagePct: parseFloat(e.target.value) || 0 }))}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                placeholder="0.000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sale Wastage (%)</label>
              <input
                type="number" step="0.001" min="0" max="100"
                value={newCategory.saleWastagePct}
                onChange={(e) => setNewCategory(prev => ({ ...prev, saleWastagePct: parseFloat(e.target.value) || 0 }))}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                placeholder="0.000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Purchase Making (₹/g)</label>
              <input
                type="number" step="0.01" min="0"
                value={newCategory.purchaseMakingPerG}
                onChange={(e) => setNewCategory(prev => ({ ...prev, purchaseMakingPerG: parseFloat(e.target.value) || 0 }))}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sale Making (₹/g)</label>
              <input
                type="number" step="0.01" min="0"
                value={newCategory.saleMakingPerG}
                onChange={(e) => setNewCategory(prev => ({ ...prev, saleMakingPerG: parseFloat(e.target.value) || 0 }))}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Certification Charges (₹/carat)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newCategory.certificationCharges}
                onChange={(e) => setNewCategory(prev => ({ ...prev, certificationCharges: parseFloat(e.target.value) || 0 }))}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="flex space-x-2 mt-4">
            <button
              onClick={handleAddCategory}
              disabled={!newCategory.name || (categoryType === 'child' && (!newCategory.code || !newCategory.parentId))}
              className={`px-4 py-2 rounded font-medium flex items-center space-x-1 ${newCategory.name && (categoryType === 'parent' || (newCategory.code && newCategory.parentId))
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
            >
        <Save size={16} />
        <span>
          {categoryType === 'parent' ? 'Save Jewelry Category' : 'Save Product Type'}
        </span>
            </button>
            <button
              onClick={() => {
                setShowAddCategoryForm(false);
                setNewCategory({
                  name: '',
                  code: '',
                  description: '',
                  wastageCharges: 0,
                  makingCharges: 0,
                  certificationCharges: 0,
                  purchaseWastagePct: 0,
                  purchaseMakingPerG: 0,
                  saleWastagePct: 0,
                  saleMakingPerG: 0,
                  parentId: null,
                  type: 'parent'
                });
                setCategoryType('parent');
                setShowCreateNewMaterial(false);
                setNewMaterialName('');
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 flex items-center space-x-1"
            >
              <X size={16} />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      )}

      {/* Categories Table */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-6 py-3 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Category Hierarchy</h3>
              <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center">
                <Gem className="mr-1 text-blue-500" size={16} />
                <span>Jewelry Categories</span>
              </div>
              <div className="flex items-center">
                <Award className="mr-1 text-green-500" size={16} />
                <span>Product Types</span>
              </div>
              {/* Removed help text to avoid duplicate matches */}
            </div>
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wastage % <span className="block text-blue-400 normal-case font-normal">Purchase / Sale</span></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Making ₹/g <span className="block text-blue-400 normal-case font-normal">Purchase / Sale</span></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certification (₹)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {getParentCategories().map(category => renderCategoryRow(category))}
          </tbody>
        </table>
      </div>

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg" data-testid="parent-categories-card">
          <div className="flex items-center justify-between">
            <div>
                <p className="text-blue-600 text-sm font-medium">Categories Overview</p>
              <p className="text-2xl font-bold text-blue-700" data-testid="parent-categories-count">{getParentCategories().length}</p>
            </div>
            <Gem className="text-blue-500" size={32} />
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg" data-testid="product-types-card">
          <div className="flex items-center justify-between">
            <div>
                <p className="text-green-600 text-sm font-medium">Product Types</p>
              <p className="text-2xl font-bold text-green-700" data-testid="product-types-count">
                {getAllChildCategories().length}
              </p>
            </div>
            <Award className="text-green-500" size={32} />
          </div>
        </div>

  <div className="bg-orange-50 p-4 rounded-lg" data-testid="avg-wastage-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-sm font-medium">Avg Wastage</p>
              <p className="text-2xl font-bold text-orange-700" data-testid="avg-wastage-value">
                {jewelryCategories.length > 0
                  ? (jewelryCategories.reduce((sum, cat) => sum + (cat.wastage_charges || 0), 0) / jewelryCategories.length).toFixed(1)
                  : 0
                }%
              </p>
            </div>
              {/* Removed extraneous action span */}
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg" data-testid="total-categories-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Total Categories</p>
              <p className="text-2xl font-bold text-purple-700" data-testid="total-categories-count">
                {jewelryCategories.length}
              </p>
            </div>
            <Package className="text-purple-500" size={32} />
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <h4 className="font-semibold text-yellow-800 mb-2">Hierarchical Category Guide:</h4>
          <div className="text-sm text-yellow-700 space-y-1">
            <p>• <strong>Quick Add:</strong> Use the quick action for faster workflow - most common operation</p>
            <p>• <strong>Create Category On-the-fly:</strong> Instantly create categories as needed</p>
            <p>• <strong>Parent Categories:</strong> Diamond, Gold, Kundan, etc. - No codes needed, used for grouping</p>
          <p>• <strong>Jewelry Types:</strong> Necklace, Ring, Earring, etc. - Require unique codes for identification</p>
          <p>• <strong>Expand/Collapse:</strong> Click the arrow to expand parent categories and see their jewelry types</p>
          <p>• <strong>Code Format:</strong> Only jewelry types need codes like DIA-N (Diamond Necklace), GOLD-R (Gold Ring)</p>
          <p>• <strong>Charges:</strong> Set material-specific wastage and making charges for accurate pricing</p>
          <p>• <strong>Delete Protection:</strong> Cannot delete parent categories that have subcategories</p>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagement;